import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from '@core/models/subscription.model';
import { SubscriptionBadgeService, SubscriptionBadgeConfig } from '../subscription-badge.service';
import { TypedFormGroup } from '@feature/forms/form-model';

/**
 * Component that displays a circular subscription tier badge indicator.
 * Designed to be overlaid on avatars or displayed as a standalone indicator.
 *
 * For LP subscriptions: Shows tier-specific icons (Free, Standard, Professional, Enterprise)
 * For GP subscriptions (KAP-1849): Shows tier-specific icons the same way (Free,
 * Basic, Professional, Enterprise) - no longer derived from add-ons.
 */
@Component({
  selector: 'wai-subscription-tier-badge',
  templateUrl: './subscription-tier-badge.component.html',
  styleUrls: ['./subscription-tier-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubscriptionTierBadgeComponent implements OnInit, OnChanges, DoCheck {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() subscription: Subscription | null = null;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  public badgeConfig: SubscriptionBadgeConfig | null = null;
  public isLP: boolean = false;
  public isGP: boolean = false;
  public hasPaymentIssue: boolean = false;

  private previousBadgeSignatureHash: string = '';

  constructor(
    private badgeService: SubscriptionBadgeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Initialize badge config if subscription is already set
    if (this.subscription) {
      this.updateBadgeConfig();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subscription']) {
      this.updateBadgeConfig();
    }
  }

  ngDoCheck(): void {
    // Detect changes to nested properties (tier code, addons array) even if
    // the subscription object reference doesn't change - important for
    // OnPush change detection. Tier code is included (KAP-1849) because it's
    // now the actual badge signal for GP, not just addons.
    if (this.subscription) {
      const currentHash = this.getBadgeSignatureHash(this.subscription);
      if (currentHash !== this.previousBadgeSignatureHash) {
        this.previousBadgeSignatureHash = currentHash;
        this.updateBadgeConfig();
      }
    }
  }

  private getBadgeSignatureHash(subscription: Subscription): string {
    const addons = subscription.addons || subscription.activeAddons || [];
    const addonsHash = addons
      .map((addon) => `${addon.uid}:${addon.isActive}:${addon.addonType}`)
      .sort()
      .join('|');
    return `${subscription.tier?.code ?? ''}#${addonsHash}`;
  }

  private updateBadgeConfig(): void {
    this.badgeConfig = this.badgeService.getBadgeConfig(this.subscription);
    this.isLP = this.subscription?.tier?.tierType === 'lp';
    this.isGP = this.subscription?.tier?.tierType === 'gp';
    this.hasPaymentIssue = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired'].includes(
      (this.subscription?.paymentStatus || '').toLowerCase(),
    );

    // Force change detection to ensure UI updates
    this.cdr.markForCheck();
  }
}
